// eslint-disable-next-line n/no-extraneous-import
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import { ScaleLoader } from 'react-spinners'
import * as THREE from 'three'
import OrbitControlsModule from 'three-orbit-controls'
import STLLoaderModule from 'three-stl-loader'

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
const STLLoader = STLLoaderModule(THREE)
// eslint-disable-next-line @typescript-eslint/no-unsafe-call
const OrbitControls = OrbitControlsModule(THREE)

interface STLViewerProps {
  className?: string
  url?: string
  file?: object
  width?: number
  height?: number
  backgroundColor?: string
  modelColor?: string
  sceneClassName?: string
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSceneRendered?: Function
}

class STLViewer extends Component<STLViewerProps> {
  static propTypes = {
    className: PropTypes.string,
    url: PropTypes.string,
    file: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
    backgroundColor: PropTypes.string,
    modelColor: PropTypes.string,
    sceneClassName: PropTypes.string,
    onSceneRendered: PropTypes.func,
  }

  static defaultProps = {
    backgroundColor: '#EAEAEA',
    modelColor: '#B92C2C',
    height: 900,
    width: 900,
    rotate: true,
    orbitControls: true,
    sceneClassName: '',
  }

  componentDidMount() {
    this.renderModel(this.props)
  }

  renderModel(props: any) {
    let camera: any, scene: any, renderer: any, mesh, distance: any, controls
    const {
      url,
      file,
      width,
      height,
      modelColor,
      backgroundColor,
      orbitControls,
      sceneClassName,
      onSceneRendered,
    } = props
    let xDims, yDims, zDims
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this

    // eslint-disable-next-line prefer-const
    scene = new THREE.Scene()
    // eslint-disable-next-line prefer-const
    distance = 10000
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
    directionalLight.position.x = 0
    directionalLight.position.y = 1
    directionalLight.position.z = 0
    directionalLight.position.normalize()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    scene.add(directionalLight)

    const ambientLight = new THREE.AmbientLight(0x404040) // soft white light
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    scene.add(ambientLight)

    const onLoad = (geometry: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      geometry.computeFaceNormals()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      geometry.computeVertexNormals()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      geometry.center()

      mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshLambertMaterial({
          /* overdraw: true, */
          color: modelColor,
        })
      )

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      geometry.computeBoundingBox()
      xDims = geometry.boundingBox.max.x - geometry.boundingBox.min.x
      yDims = geometry.boundingBox.max.y - geometry.boundingBox.min.y
      zDims = geometry.boundingBox.max.z - geometry.boundingBox.min.z

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      scene.add(mesh)

      camera = new THREE.PerspectiveCamera(30, width / height, 1, distance)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      camera.position.set(0, 0, Math.max(xDims * 3, yDims * 3, zDims * 3))

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      scene.add(camera)

      renderer = new THREE.WebGLRenderer({
        preserveDrawingBuffer: true,
        antialias: true,
      })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      renderer.setSize(width, height)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      renderer.setClearColor(backgroundColor, 1)
      renderer.domElement.className = sceneClassName

      if (orbitControls) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,react/no-find-dom-node
        controls = new OrbitControls(camera, ReactDOM.findDOMNode(component))
        controls.enableKeys = false
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        controls.addEventListener('change', orbitRender)
      }

      // eslint-disable-next-line react/no-find-dom-node
      const container = ReactDOM.findDOMNode(this)
      if (container) {
        container.replaceChild(renderer.domElement, container.firstChild as Node)
      }

      render()

      if (typeof onSceneRendered === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,react/no-find-dom-node
        onSceneRendered(ReactDOM.findDOMNode(renderer.domElement))
      }
    }

    const onProgress = (xhr: any) => {
      if (xhr.lengthComputable) {
        const percentComplete = (xhr.loaded / xhr.total) * 100
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const loader = new STLLoader()

    if (file) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      loader.loadFile(file, onLoad, onProgress)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      loader.load(url, onLoad, onProgress)
    }

    const render = () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      renderer.render(scene, camera)
    }

    const orbitRender = () => {
      render()
    }
  }

  shouldComponentUpdate(nextProps: any, nextState: any) {
    if (JSON.stringify(nextProps) === JSON.stringify(this.props)) {
      return false
    }
    return true
  }

  componentDidUpdate(nextProps: any, nextState: any) {
    this.renderModel(nextProps)
  }

  componentDidCatch(error: any, info: any) {
    console.log(error, info)
  }

  render() {
    return (
      <div
        className={/* this.props.className */ ''}
        style={{
          width: this.props.width,
          height: this.props.height,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ScaleLoader color={'#123abc'} loading={true} />
        </div>
      </div>
    )
  }
}

export default STLViewer
